import {
  bindingFor,
  NAMED_TEMPLATE_VARIABLES,
  TEMPLATE_VARIABLE_SOURCE_LABEL,
  TEMPLATE_VARIABLE_SOURCES,
  type TemplateVariableMap,
  type TemplateVariableSource,
  templateVariableKey,
} from "@orbis/shared";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { useUpdateTemplate } from "../../lib/queries";
import type { Template } from "../../lib/types";

/** Explains which variable names fill themselves and how to bind numbered ones. */
export function TemplateVariablesHelp() {
  return (
    <details className="disclosure">
      <summary>¿Cómo se llenan las variables solas?</summary>
      <div className="disclosure-body">
        <p className="muted">
          Al crear la plantilla en Meta, usa estas variables por nombre y Orbis las llena al elegirla en el chat. El
          asesor puede cambiarlas antes de enviar.
        </p>
        <table className="table variables-table">
          <thead>
            <tr>
              <th>Escribe en la plantilla</th>
              <th>Orbis pone</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(NAMED_TEMPLATE_VARIABLES).map(([name, source]) => (
              <tr key={name}>
                <td className="mono">{`{{${name}}}`}</td>
                <td>{TEMPLATE_VARIABLE_SOURCE_LABEL[source]}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted">
          Ejemplo:{" "}
          <span className="mono">
            Hola {"{{cliente}}"}, soy {"{{asesor}}"} de {"{{empresa}}"}.
          </span>
        </p>
        <p className="muted">
          Si la plantilla usa números (<span className="mono">{"{{1}}"}</span>, <span className="mono">{"{{2}}"}</span>
          …), toca <strong>Variables</strong> en su fila y elige de dónde sale cada una, o escribe un valor fijo. Los
          números de pedido, caso o cotización se llenarán solos cuando existan esos módulos.
        </p>
      </div>
    </details>
  );
}

type Choice = "" | TemplateVariableSource | "fixed";

/** Dialog to choose where each variable of one template comes from. */
export function TemplateVariablesDialog({ template, onClose }: { template: Template; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const update = useUpdateTemplate();
  const toast = useToast();
  const vars = [
    ...template.headerVariables.map((name) => ({ scope: "header" as const, name })),
    ...template.bodyVariables.map((name) => ({ scope: "body" as const, name })),
  ];
  const initial = (scope: "body" | "header", name: string) => {
    const own = template.variableMap?.[templateVariableKey(scope, name)];
    if (!own) return { choice: "" as Choice, fixed: "" };
    return "fixed" in own ? { choice: "fixed" as Choice, fixed: own.fixed } : { choice: own.source, fixed: "" };
  };
  const [state, setState] = useState(() =>
    Object.fromEntries(vars.map((v) => [templateVariableKey(v.scope, v.name), initial(v.scope, v.name)])),
  );

  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const map: TemplateVariableMap = {};
    for (const [key, { choice, fixed }] of Object.entries(state)) {
      if (choice === "fixed" && fixed.trim()) map[key] = { fixed: fixed.trim() };
      else if (choice && choice !== "fixed") map[key] = { source: choice };
    }
    try {
      await update.mutateAsync({ id: template.id, variableMap: map });
      toast("Variables guardadas");
      dialog.current?.close();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudieron guardar.");
    }
  }

  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="tv-title">
      <form className="dialog-form" onSubmit={onSubmit}>
        <h3 id="tv-title">Variables de {template.name}</h3>
        <p className="muted">
          Elige de dónde sale cada variable. Las que dejes en "El asesor la escribe" se llenan a mano al enviar.
        </p>
        {vars.map(({ scope, name }) => {
          const key = templateVariableKey(scope, name);
          const value = state[key] ?? { choice: "", fixed: "" };
          const named = !template.variableMap?.[key] && bindingFor({}, scope, name);
          return (
            <div className="field" key={key}>
              <label htmlFor={`tv-${key}`}>
                {scope === "header" ? "Encabezado" : "Variable"} <span className="mono">{`{{${name}}}`}</span>
                {named && <span className="faint"> · se llena sola por su nombre</span>}
              </label>
              <select
                className="select"
                id={`tv-${key}`}
                value={value.choice}
                onChange={(e) => setState({ ...state, [key]: { ...value, choice: e.target.value as Choice } })}
              >
                <option value="">{named ? "Por su nombre (automática)" : "El asesor la escribe"}</option>
                {TEMPLATE_VARIABLE_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {TEMPLATE_VARIABLE_SOURCE_LABEL[s]}
                  </option>
                ))}
                <option value="fixed">Valor fijo…</option>
              </select>
              {value.choice === "fixed" && (
                <input
                  className="input"
                  aria-label={`Valor fijo de {{${name}}}`}
                  placeholder="Por ejemplo: la tienda de Alajuela"
                  maxLength={scope === "header" ? 60 : 1024}
                  value={value.fixed}
                  onChange={(e) => setState({ ...state, [key]: { ...value, fixed: e.target.value } })}
                />
              )}
            </div>
          );
        })}
        <div className="row">
          <button className="btn" type="button" onClick={() => dialog.current?.close()}>
            Cancelar
          </button>
          <button className="btn btn-primary" type="submit" disabled={update.isPending}>
            {update.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
